import toast from 'react-hot-toast';
import { X, CheckCircle2, XCircle, Info, AlertTriangle } from 'lucide-react';

export const customToast = {
  success: (message: string, options?: any) => {
    return toast.custom((t) => (
      <div className={`
        relative flex items-start p-4 pr-12 min-w-80 max-w-md
        bg-white dark:bg-gray-800
        border border-green-200 dark:border-green-800
        rounded-xl shadow-lg dark:shadow-xl
        transform transition-all duration-300 ease-out overflow-hidden
        ${t.visible ? 'animate-in slide-in-from-bottom-2' : 'animate-out slide-out-to-bottom-2'}
      `}>
        <div className="flex-shrink-0 mt-0.5">
          <CheckCircle2 className="w-5 h-5 text-green-500" />
        </div>
        <div className="ml-3 flex-1 pt-0.5">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-5">
            {message}
          </p>
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          className="
            absolute top-3 right-3 p-1.5 
            rounded-lg transition-all duration-200
            hover:bg-gray-100 dark:hover:bg-gray-700
            focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
            group
          "
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-500 animate-shrink" 
             style={{ animationDuration: '4000ms' }} />
      </div>
    ), {
      duration: 4000,
      position: 'bottom-right',
      ...options
    });
  },

  error: (message: string, options?: any) => {
    return toast.custom((t) => (
      <div className={`
        relative flex items-start p-4 pr-12 min-w-80 max-w-md
        bg-white dark:bg-gray-800
        border border-red-200 dark:border-red-800
        rounded-xl shadow-lg dark:shadow-xl
        transform transition-all duration-300 ease-out overflow-hidden
        ${t.visible ? 'animate-in slide-in-from-bottom-2' : 'animate-out slide-out-to-bottom-2'}
      `}>
        <div className="flex-shrink-0 mt-0.5">
          <XCircle className="w-5 h-5 text-red-500" />
        </div>
        <div className="ml-3 flex-1 pt-0.5">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-5">
            {message}
          </p>
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          className="
            absolute top-3 right-3 p-1.5 
            rounded-lg transition-all duration-200
            hover:bg-gray-100 dark:hover:bg-gray-700
            focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
            group
          "
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-500 animate-shrink" 
             style={{ animationDuration: '5000ms' }} />
      </div>
    ), {
      duration: 5000,
      position: 'bottom-right',
      ...options
    });
  },

  info: (message: string, options?: any) => {
    return toast.custom((t) => (
      <div className={`
        relative flex items-start p-4 pr-12 min-w-80 max-w-md
        bg-white dark:bg-gray-800
        border border-blue-200 dark:border-blue-800
        rounded-xl shadow-lg dark:shadow-xl
        transform transition-all duration-300 ease-out overflow-hidden
        ${t.visible ? 'animate-in slide-in-from-bottom-2' : 'animate-out slide-out-to-bottom-2'}
      `}>
        <div className="flex-shrink-0 mt-0.5">
          <Info className="w-5 h-5 text-blue-500" />
        </div>
        <div className="ml-3 flex-1 pt-0.5">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-5">
            {message}
          </p>
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          className="
            absolute top-3 right-3 p-1.5 
            rounded-lg transition-all duration-200
            hover:bg-gray-100 dark:hover:bg-gray-700
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            group
          "
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500 animate-shrink" 
             style={{ animationDuration: '4000ms' }} />
      </div>
    ), {
      duration: 4000,
      position: 'bottom-right',
      ...options
    });
  },

  warning: (message: string, options?: any) => {
    return toast.custom((t) => (
      <div className={`
        relative flex items-start p-4 pr-12 min-w-80 max-w-md
        bg-white dark:bg-gray-800
        border border-amber-200 dark:border-amber-800
        rounded-xl shadow-lg dark:shadow-xl
        transform transition-all duration-300 ease-out overflow-hidden
        ${t.visible ? 'animate-in slide-in-from-bottom-2' : 'animate-out slide-out-to-bottom-2'}
      `}>
        <div className="flex-shrink-0 mt-0.5">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
        </div>
        <div className="ml-3 flex-1 pt-0.5">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-5">
            {message}
          </p>
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          className="
            absolute top-3 right-3 p-1.5 
            rounded-lg transition-all duration-200
            hover:bg-gray-100 dark:hover:bg-gray-700
            focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2
            group
          "
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-amber-500 animate-shrink" 
             style={{ animationDuration: '4000ms' }} />
      </div>
    ), {
      duration: 4000,
      position: 'bottom-right',
      ...options
    });
  }
};

export default customToast;